import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from '@hello-pangea/dnd';
import { Table, TableBody } from '@mui/material';
import { Alert } from '@design-system/components/alert/alert';
import { Button } from '@design-system/components/button/button';
import { Modal } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import { DragTableRow } from '@client/new/components/DragTable/DragTable';
import {
  ColumnSchema,
  Row,
  StandardTable,
} from '@client/new/components/StandardTable/StandardTable';
import { Cell, CellProps } from '@client/new/components/TableShared/Cell';
import {
  ScheduleGroup,
  ScheduledItem,
  formatGroupTime,
} from '../model/scheduleGroups.domain';
import { useScheduleGroups } from '../model/useScheduleGroups';
import { PublishDateModal } from './PublishDateModal';
import {
  calendarIcon,
  columnLabels,
  countPill,
  group,
  heading,
  modalActions,
  row,
  stack,
  table,
} from './scheduleGroups.css';

const unscheduleDescription = (g: ScheduleGroup) => {
  const workouts =
    g.items.length === 1 ? 'The workout' : `The ${g.items.length} workouts`;
  return `${workouts} scheduled for ${formatGroupTime(
    g.publishedAt,
  )} will be unscheduled and moved to Drafts. Nothing is deleted.`;
};

export type ScheduleGroupsProps = {
  programId: number;
  programName: string;
  items: Array<ScheduledItem>;
  readonly: boolean;
  onChanged: () => void;
};

const schema: Array<ColumnSchema> = [
  { width: 'stretchable' },
  { width: 50, justify: 'right' },
];

const withOrder = (cell: CellProps, order: number): CellProps => {
  if (cell.type === 'Head')
    return { ...cell, value: { ...cell.value, showOrder: true, order } };
  if (cell.type === 'Items')
    return { ...cell, value: cell.value.map((c) => withOrder(c, order)) };
  return cell;
};

export const ScheduleGroups = (p: ScheduleGroupsProps) => {
  const vm = useScheduleGroups(p);
  const isBusy = vm.state.type === 'fetching';

  const toRow = (item: ScheduledItem, index: number): Row<number> => ({
    ...item.row,
    cells: item.row.cells.map((cell) => withOrder(cell, index + 1)),
  });

  const onDragEnd = ({ draggableId, source, destination }: DropResult) => {
    if (!destination) return;
    vm.drop({
      workoutId: Number(draggableId),
      fromKey: Number(source.droppableId),
      toKey: Number(destination.droppableId),
      index: destination.index,
    });
  };

  const renderHeader = (g: ScheduleGroup) => (
    <div className={row}>
      <div className={heading}>
        <FontAwesomeIcon icon={faCalendar} className={calendarIcon} />
        <Typography size="large" color="N-100">
          {formatGroupTime(g.publishedAt)}
        </Typography>
        <Typography color="N-100" className={countPill}>
          {g.items.length} {g.items.length === 1 ? 'workout' : 'workouts'}
        </Typography>
      </div>
      {!p.readonly && (
        <Cell
          type="Menu"
          value={{
            items: [
              { text: 'Edit date & time', onClick: () => vm.openReschedule(g) },
              { text: 'Remove group', onClick: () => vm.openUnschedule(g) },
            ],
          }}
        />
      )}
    </div>
  );

  const renderDraggableGroup = (g: ScheduleGroup) => (
    <Table className={table}>
      <Droppable droppableId={String(g.key)}>
        {(droppable) => (
          <TableBody ref={droppable.innerRef} {...droppable.droppableProps}>
            {g.items.map((item, index) => (
              <Draggable
                key={item.id}
                draggableId={String(item.id)}
                index={index}
                isDragDisabled={isBusy}
              >
                {(provided, snapshot) => (
                  <DragTableRow
                    schema={schema}
                    provided={provided}
                    snapshot={snapshot}
                    index={index}
                    {...toRow(item, index)}
                  />
                )}
              </Draggable>
            ))}
            {droppable.placeholder}
          </TableBody>
        )}
      </Droppable>
    </Table>
  );

  const groupsView = vm.groups.map((g) => (
    <div key={g.key} className={group} data-testid={`scheduleGroup-${g.key}`}>
      {renderHeader(g)}
      {p.readonly ? (
        <StandardTable
          schema={schema}
          rows={g.items.map((i, index) => ({
            ...i.row,
            cells: i.row.cells.map((c) => withOrder(c, index + 1)),
          }))}
        />
      ) : (
        renderDraggableGroup(g)
      )}
    </div>
  ));

  return (
    <div className={stack}>
      {vm.state.type === 'fetch-error' && (
        <Alert variant="error" title={vm.state.value} />
      )}
      <div className={columnLabels}>
        <Typography size="small">WORKOUT NAME</Typography>
        {!p.readonly && <Typography size="small">ACTION</Typography>}
      </div>
      {p.readonly ? (
        groupsView
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>{groupsView}</DragDropContext>
      )}
      {vm.modal?.type === 'reschedule' && (
        <PublishDateModal
          title="Edit date & time"
          group={vm.modal.group}
          scheduled={vm.groups.flatMap((g) => g.items)}
          programName={p.programName}
          onClose={vm.closeModal}
          onConfirm={vm.confirmReschedule}
        />
      )}
      {vm.modal?.type === 'unschedule' && (
        <Modal
          isOpen
          onClose={vm.closeModal}
          title="Remove this group?"
          description={unscheduleDescription(vm.modal.group)}
        >
          <div className={modalActions}>
            <Button variant="text" color="neutral" onClick={vm.closeModal}>
              Cancel
            </Button>
            <Button
              color="error"
              onClick={vm.confirmUnschedule}
              testId="confirmUnschedule"
            >
              Remove group
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};
